import Application from 'js/lib/app';
import Config from 'js/config';
import _ from 'underscore';
import $ from 'jquery';
import Promise from 'promise';
import Backbone from 'backbone';
import analytics from 'js/analytics';
import Head from 'js/lib/head';
import SocketIO from 'socket.io-client';
import Api from 'js/api';
import React from 'react';
import { getCookieValue, setLangCookieAndReload, setReferrerPathCookie } from 'js/utils/cookie';
import Context from 'js/models/context';
import Notifications from 'js/collections/notifications';
import ErrorPage from 'jsx/pages/error';
import { actions } from 'virgo';
import BetelgeuseMixin from 'js/mixins/betelgeuse';

const { userFlowActionsCreator: { login: { loginSuccess } } } = actions;

/**
 * Create our Betelgeuse App
 */
var Betelgeuse = Application.extend();

/**
 * Flag to force context update on next route
 */
Betelgeuse.prototype.forceContext = false;

/**
 * Flag to say whether we are currently handling a dispatch request
 */
Betelgeuse.prototype.dispatching = false;

/**
 * Interval id for message/notifications checks
 */
Betelgeuse.prototype.updateInterval = null;

/**
 * Was the user active at any point through the life of this app?
 */
Betelgeuse.prototype.wasActive = false;

/**
 * Global notifications collection for checking updates
 */
Betelgeuse.prototype.notifications = null;

/**
 * The step number of pages the user has visited (not persistant across tabs/sessions)
 */
Betelgeuse.prototype.pageStep = 0;

/**
 * Flag to determine whether we have handled our first context resolution
 */
Betelgeuse.prototype.handledFirstContext = false;

/**
 * Initialize betelgeuse
 */
Betelgeuse.prototype.initialize = function() {
  Betelgeuse.__super__.initialize.apply(this, arguments); // Call super

  // Create a new context instance
  this.context = new Context();

  // Initialize head instance
  this.head = new Head();

  // Initialise notifications
  this.notifications = new Notifications();

  // Open a web socket to Meteor
  this.openMeteorClient(Config.build.meteorUrl, Config.build.meteorPath, function(err) {
    if (!err) {
      return;
    }
    if (this.meteorClient) {
      this.meteorClient.disconnect();
    }
    this.startUpdating();
  }.bind(this));

  // Register push-notification handler for messages and notifications
  this.registerOnMessagesHandler();
  this.registerOnNotificationsHandler();

  // App event bindings
  this.listenTo(Backbone, 'messages:invalidate', this.doUpdate);
  this.listenTo(this.context, 'sync', this.onContextSync);
  this.listenToOnce(this, 'dispatch:controller', function() {
    this.handleParams(this.router.res.params);
  });

  this.listenTo(this, 'dispatch:action', function() {
    var isActive = this.context.isActive();
    const userType = isActive ? 'Member' : 'Visitor';
    if (this.state) {
      analytics.trackPageView(userType);
      analytics.trackPageForPartner();
    }
  });

  $('body').toggleClass('OS-ios', /(iPad|iPhone|iPod)/g.test(navigator.userAgent))
    .toggleClass('OS-android', /(android)/g.test(navigator.userAgent));
};

/**
 * Triggers a modal if specific params are in the url
 */
Betelgeuse.prototype.handleParams = function(params) {
  const { branch, recipient } = params;

  // [PROD-633] For visitors/members, remove recipient from url when coming from email notifications
  // on all routes except verify route as we redirect to solaris homepage to show login modal
  const isNotVerifyPage = this.router.res && this.router.res.action !== 'verify';
  if (recipient && isNotVerifyPage) {
    const newParamsObject = _.object(Object.entries(params).filter(param => !param.includes('recipient')));
    const newParamsString = BetelgeuseMixin.generateParams(newParamsObject);
    this.router.navigate(`${location.pathname}${newParamsString}`);
  }

  if (Config.env.devOrStaging && branch) {
    window.HU_TEST_BRANCH = branch.toUpperCase();
  }
};

/**
 * Parse resolution object to take context into account
 */
Betelgeuse.prototype.parseRes = function(r) {
  var res = _.clone(r);

  if (this.context.isGroup()) {
    res.controller = 'community';
  } else if (this.context.isUser() && this.context.isActive() && res.action == 'index') {
    window.location.replace(`${location.origin}/user/${res.params.context}`);
  }

  return res;
};

/**
 * Return a failed resolution
 */
Betelgeuse.prototype.failParseRes = function(res) {
  res.action = 'route';
  res.controller = 'fake';
  res.module = 'catchAll';
  return res;
};

/**
 * Overridden method for context functionality
  */
Betelgeuse.prototype.dispatch = function(res) {
  var contextId = res.params.context || '';

  // Set flag
  this.dispatching = true;

  // Update the context
  // TODO: Could cache context data
  this.context.update(contextId, this.forceContext).then(function(payload) {
    if (payload._status && payload._status.responseCode === '4032022') {
      this.lastRes = this.res;
      this.res = this.parseRes(res);

      this.showError(null, 403, null, {
        title: i18n("You don't have access to this community"),
        subTitle: <span>
          {i18n('If you think you should have access please email')}
          {' '}
          support@healthunlocked.com
        </span>,
      });

      this.dispatching = false;
      return true;
    }

    // Continue with default functionality
    Betelgeuse.__super__.dispatch.call(this, res);

    // Set flag
    this.dispatching = false;
  }, function(status) {

    // For now we assume a 'success' status which broke (because of errors) will simply require a login
    // so redirect the users to the login page
    if (status && status.success) {
      window.location.replace(`${location.origin}/login${location.pathname}${location.search}${location.hash}`);
    } else {
      this.showError(new Error('Failed to resolve context.'));
    }

    // Set flag
    this.dispatching = false;
  }, this);

  // Revert context forcing
  this.forceContext = false;
};

/**
 * Set the given page contents (pass a react instance)
 * Run on page load and on route change
 */
Betelgeuse.prototype.updatePage = function(page, pageData) {
  // Get rid of any current modal. We won t need it going through to a new page
  this.setModal(null);

  Betelgeuse.__super__.updatePage.apply(this, arguments); // Call super

  if (!pageData) {
    pageData = {};
  }

  // Handle special 'modular' prop
  $('body').toggleClass('body-modular', !!pageData.modular);

  // Destroy Google Ad Manager ads on route change
  googletag.cmd.push(() => googletag.destroySlots());

  // For Google Ad Manager ads, poll page is the only page where we have delayAds
  if (pageData.delayAds instanceof Promise) {
    pageData.delayAds.then(function() {
      Backbone.trigger('advert:refresh');
    });
  } else {
    Backbone.trigger('advert:refresh');
  }

  // Scroll to top
  if (!pageData.preventScroll) {
    Backbone.trigger('view:scrollToTop');
  }
};

Betelgeuse.prototype.setModal = function(modal) {
  // BB-940: ugly hack to fix flickering inputs on ios by having a height at 100%
  // only applied to modal otherwise we'll always see the safari bars (BB-1088)
  if (/(iPad|iPhone|iPod)/g.test(navigator.userAgent)) {
    if (modal) {
      $('html').addClass('OS-ios-modal-html');
      $('body').addClass('OS-ios-modal-body');
    } else {
      $('html').removeClass('OS-ios-modal-html');
      $('body').removeClass('OS-ios-modal-body');
    }
  }
  this.app.setState({ modal });
};

/**
 * Set current alert
 * NOTE: Every alert passed is cloned and made unique using a 'key' prop
 */
Betelgeuse.prototype.setAlert = function(alert) {

  alert = alert ? React.cloneElement(alert, {
    key: _.uniqueId('alert'),
    global: true,
    // Clear this alert if it is in use when it is done
    onFinished: function() {
      // If the alert is actually in use
      if (this.app.state.alert === alert) {
        this.app.setState({ alert: null });
      }
    }.bind(this),
    app: this,
  }) : null;

  this.app.setState({
    alert: alert,
  });
};

/**
 * Show an error page
 */
Betelgeuse.prototype.showError = function(error, code, redirect, props = {}) {
  if (console && console.warn) {
    console.warn(error, code);
  }

  this.updatePage(ErrorPage, {
    ...props,
    error: error,
    code: code,
    redirect: redirect,
  });

  document.title = 'HealthUnlocked [' + (code || 500) + ']';
};

/**
 * Set the context the modal was opened from (for analytics tracking)
 */
Betelgeuse.prototype.updateSignupContext = function(signupContext) {
  this.context.setSignupContext(signupContext);
};

/**
 * Called whenever we update the context
 */
Betelgeuse.prototype.onContextSync = function() {
  var isActive = this.context.isActive();
  const { session, user, locale } = this.context.getContext();
  this.updateVirgoContext(session, isActive, user, locale.split('_')[0]);

  // Ensure 'wasActive' is set correctly
  // We should be storing wasActive in sessionStorage really so it persists across tabs...
  if (isActive) {
    this.wasActive = true;
  }

  // If the current state needs to be invalidated as context changes
  // Also checks we aren t already dispatching which will result in a state change anyway
  if (!this.dispatching && this.state && this.state.data && this.state.data.contextInvalidation) {
    this.dispatch(this.router.res);
  }

  // Check for any user language settings
  var cookieLang = getCookieValue('huLang');
  var userLang = this.context.getContext().locale.split('_')[0];

  // If the user lang differs to what we have set
  if (userLang !== (cookieLang || Config.defaultLanguage.short)) {
    setLangCookieAndReload(userLang);
  }

  setReferrerPathCookie();

  // Start or stop checking messages
  this.stopUpdating();
  if (isActive) {
    this.doUpdate();
    if (this.meteorClient) {
      this.meteorClient.connect();
    }
  } else {
    if (this.meteorClient) {
      this.meteorClient.disconnect();
    }
  }

  // Only bother fetching 'top communities' for logged out users when they first land on the site
  if (!this.handledFirstContext && !isActive) {
  }

  this.handledFirstContext = true;
};

/**
 * Register onclose and onerror WebSocket handlers.
 * Tries to reestablish connection with Meteor. When an error occurs
 * falls back to polling.
 */
Betelgeuse.prototype.openMeteorClient = function(url, path, callback) {
  if (typeof WebSocket === 'undefined') {
    callback(new Error('WebSockets not supported'));
    return;
  }

  if (this.meteorClient && this.meteorClient.connected) {
    callback(null);
    return;
  }

  if (!this.meteorClient) {
    this.meteorClient = SocketIO.connect(url, {
      path: path,
      reconnectionAttempts: 3,
    });
  } else {
    this.meteorClient.connect();
  }

  this.meteorClient.on('connect_error', callback);
  this.meteorClient.on('reconnect_failed', function() {
    callback(new Error('Unable to reconnect to Meteor'));
  });
};

/**
 * Listens to "messages.unread-count" event from Meteor,
 * and updates unreadMessages according to received data
 */
Betelgeuse.prototype.registerOnMessagesHandler = function() {
  const unreadHandler = (data) => {
    let unreadMessages = this.app.state.unreadMessages;
    switch (data.action) {
      // to support compatibility with corona, meteor might still sending 'inc' value
      // we don't want to support it anymore and only increase the value with "+"
      case '+':
        unreadMessages += data.count;
        break;
      case 'dec':
        unreadMessages -= data.count;
        break;
      case 'set':
        unreadMessages = data.count;
        break;
    }

    this.app.setState({
      unreadMessages: Math.max(0, unreadMessages),
    }, this.updateTitle.bind(this));
  };

  if (this.meteorClient) {
    this.meteorClient.on('messages.unread-count', unreadHandler);
  }
};

/**
 * Listens to "notifications.unread-count" event from Meteor,
 * and updates unreadNotifications according to received data
 */
Betelgeuse.prototype.registerOnNotificationsHandler = function() {
  const handler = (data) => {
    let unreadNotifications = this.app.state.unreadNotifications;

    switch (data.action) {
      case 'inc':
        unreadNotifications += data.count;
        break;
      case 'dec':
        unreadNotifications -= data.count;
        break;
      case 'set':
        unreadNotifications = data.count;
        break;
    }
    // set a 4s delay if we are setting the notification count (hack, etc)...
    const unreadDelay = data.action === 'set' ? 4000 : 10;
    setTimeout(() => this.app.setState({
      unreadNotifications: Math.max(0, unreadNotifications),
    }, this.updateTitle.bind(this)), unreadDelay);
  };

  if (this.meteorClient) {
    this.meteorClient.on('notifications.unread-count', handler);
  }
};

/**
 * Check for messages/notifications (loops)
 */
Betelgeuse.prototype.doUpdate = function() {
  // Ask api for unread messages and then update state
  Api.request(Config.build.apiPath + '/private/messages/unread-count').then(function(response) {
    this.app.setState({
      unreadMessages: response.unreadMessages,
    }, this.updateTitle.bind(this));
  }, this);


  this.notifications.fetchMeta().then(() => {
    this.app.setState({ unreadNotifications: this.notifications.unreadTotal });
    this.updateTitle.bind(this);
  });
};

/**
 * Change page title to include number of messages+notifications
 */
Betelgeuse.prototype.updateTitle = function() {
  const unread = this.app.state.unreadNotifications + this.app.state.unreadMessages;

  this.head.setTitlePrefix(unread > 0 ? '(' + unread + ') ' : '');
};

/**
 * Clear interval for refreshing messages/notifications
 */
Betelgeuse.prototype.stopUpdating = function() {
  clearInterval(this.updateInterval);
};

/**
 * Set interval for refreshing messages/notifications
 */
Betelgeuse.prototype.startUpdating = function() {
  this.stopUpdating();
  this.updateInterval = setInterval(this.doUpdate.bind(this), 30000);
};

/**
 * When a user requests a language change
 * @param lang
 */
Betelgeuse.prototype.updateLang = function(lang) {
  this.context.updateUserLang(lang).then(setLangCookieAndReload.bind(this, lang));
};

Betelgeuse.prototype.logout = function() {
  return this.context.logout(true).then(function() {
    // Track in analytics
    analytics.trackEvent('Account', 'Logged out');

    if (!this.router.navigate('/')) {
      return window.location.href = '/';
    }

    return true;
  }, this);
};

// needed to add context data to virgo
// virgo TODO: remove on app refactor and add virgo update to main app component/router
Betelgeuse.prototype.updateVirgoContext = function(session, isActive, user, locale) {
  const dispatch = this.app.getChildContext().store.dispatch;
  dispatch(loginSuccess({ session, isActive, user, locale }));
};

module.exports = Betelgeuse;



// WEBPACK FOOTER //
// ./app/assets/js/betelgeuse.js